import ProductCard from 'src/components/restructure/product/ProductCard'
import { useIsClient } from 'src/hooks/useIsClient'
import { sanitizeProductCustomHtml } from 'src/utils/sanitizeProductCustomHtml'

import { useVitrineLargeTabs } from './hooks/useVitrineLargeTabs'
import ProductCardSkeleton from './ProductCardSkeleton'
import Tabs from './Tabs'
import type { VitrineLargeProps } from './types'
import { isDarkTheme } from './utils/changeDarkTheme'

export default function VitrineLarge({
  theme = 'light',
  title,
  subtext,
  position,
  items = [],
}: VitrineLargeProps) {
  const isClient = useIsClient()
  const {
    activeTab,
    activeTabIndex,
    activeTabKey,
    activeTabLoading,
    activeProducts,
    allTabsWithoutProducts,
    canHideComponentByEmptyState,
    dyLoading,
    resolvedTabs,
    setActiveTabIndex,
    tabsItems,
  } = useVitrineLargeTabs({
    theme,
    position,
    items,
  })

  if (!resolvedTabs.length || !activeTab) {
    return null
  }

  if (canHideComponentByEmptyState && allTabsWithoutProducts) {
    return null
  }

  const isDarkThemeActive = isDarkTheme(theme)
  const titleComponent = title?.trim()
  const sanitizedSubtext = isClient ? sanitizeProductCustomHtml(subtext) : ''
  const shelfRef = `Vitrine: ${titleComponent}`
  const shelfEngine = activeTab.engine === 'DY' ? 'dy' : 'vtex-editorial'

  return (
    <section
      className={`${
        isDarkThemeActive
          ? 'bg-restructure-background-neutral-12 text-restructure-primary-inverted'
          : 'bg-white text-restructure-primary'
      }`}
    >
      <div className="mx-auto w-full max-w-[1440px]">
        <div className={`px-4 pt-6 md:px-0 md:pt-[69px] md:mb-8 `}>
          <div className="flex flex-col md:flex-row items-start justify-center md:justify-between md:items-center gap-2 md:gap-4 lg:gap-6">
            <div className="flex flex-col items-center md:items-start justify-center">
              {titleComponent ? (
                <h2
                  className={`text-left text-2xl font-bold leading-tight md:text-3xl lg:text-[32px] ${
                    isDarkThemeActive
                      ? 'text-restructure-primary-inverted'
                      : 'text-restructure-primary'
                  }`}
                >
                  {titleComponent}
                </h2>
              ) : null}

              {sanitizedSubtext ? (
                <div
                  className={`mt-2 text-sm md:mt-3 md:text-base [&_a]:underline ${
                    isDarkThemeActive
                      ? 'text-restructure-secondary-inverted'
                      : 'text-restructure-secondary'
                  }`}
                  suppressHydrationWarning
                  dangerouslySetInnerHTML={{ __html: sanitizedSubtext }}
                />
              ) : null}
            </div>

            <Tabs
              items={tabsItems}
              activeIndex={activeTabIndex}
              theme={theme}
              ariaLabel="Abas da vitrine"
              onChange={setActiveTabIndex}
            />
          </div>

          <div
            id={`vitrine-large-panel-${activeTabKey}`}
            role="tabpanel"
            aria-labelledby={`vitrine-large-tab-${activeTabKey}`}
            className="mt-5 md:mt-6"
          >
            {activeTabLoading || (activeTab.engine === 'DY' && dyLoading) ? (
              <ProductCardSkeleton />
            ) : (
              <ul
                className={`grid w-full grid-cols-2 gap-3 md:gap-6 lg:grid-cols-4 ${
                  isDarkThemeActive
                    ? '[&_p]:!text-restructure-primary-inverted [&_span]:!text-restructure-primary-inverted [&_h3]:!text-restructure-primary-inverted [&_a]:!text-restructure-primary-inverted'
                    : ''
                }`}
              >
                {activeProducts.map((product, index) => (
                  <li
                    key={`${activeTabKey}-${product.productId}-${index}`}
                    className="w-full text-left"
                  >
                    <ProductCard
                      product={product}
                      index={index}
                      drag={false}
                      shelfRef={shelfRef}
                      shelfEngine={shelfEngine}
                      type="landingpage"
                      title={titleComponent}
                      isSlider
                      id={activeTab.id}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
